import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { makeStyles } from '@mui/styles';
import React from 'react';

import { DataAPIContext } from '../api';
import { CREATE, GET_LIST } from '../api/dataProvider';

/**
 * Single-pick image dialog for inserting an inline image into a rich
 * text editor (currently used by the campaign editor's "Customize for
 * email" QuillEditor).
 *
 * Two tabs: Upload new / Browse existing. On any selection — upload
 * completes successfully OR the operator clicks a tile — the dialog
 * fires ``onPick({id, url, name})`` once and closes.
 *
 * Intentionally NOT a fork of ``AttachmentImagePicker``: that one is
 * multi-select and stays open after each pick to support building a
 * list. This one is single-pick and closes immediately, so the picker
 * doesn't interrupt the inline-image-insert flow with extra clicks.
 */

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|svg|bmp)(\?.*)?$/i;
const isImageUrl = (s) => !!s && IMAGE_EXTENSIONS.test(s);

const useStyles = makeStyles((theme) => ({
    dropzone: {
        border: '2px dashed #b0b3c0',
        borderRadius: 8,
        padding: theme.spacing(4),
        textAlign: 'center',
        cursor: 'pointer',
        '&:hover': { borderColor: '#7080a0', background: '#fafbff' },
    },
    dropzoneActive: {
        borderColor: '#007AFF',
        background: '#eef4ff',
    },
}));

const InlineImagePickerDialog = ({ open, onClose, onPick }) => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const [tab, setTab] = React.useState('upload');
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState(null);
    const [dragOver, setDragOver] = React.useState(false);
    const [browseRows, setBrowseRows] = React.useState([]);
    const [browseLoading, setBrowseLoading] = React.useState(false);
    const [browseError, setBrowseError] = React.useState(null);
    const fileInputRef = React.useRef(null);

    const reset = () => {
        setUploadError(null);
        setBrowseError(null);
        setDragOver(false);
    };

    React.useEffect(() => {
        if (open) {
            reset();
            setTab('upload');
        }
    }, [open]);

    const close = () => {
        onClose();
        reset();
    };

    const finishPick = (item) => {
        if (!item || !item.url) return;
        onPick({ id: item.id, url: item.url, name: item.name });
        close();
    };

    const uploadFile = async (file) => {
        if (!file) return;
        setUploading(true);
        setUploadError(null);
        try {
            const res = await dataAPI(CREATE, 'attachment', {
                files: { file },
                data: {},
            });
            const body = res?.data || {};
            if (!body.id || !body.url) {
                setUploadError('Upload succeeded but server returned no URL.');
                return;
            }
            finishPick({ id: body.id, url: body.url, name: body.name });
        } catch (e) {
            setUploadError(e?.body?.message || e?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const onFileInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) uploadFile(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadFile(file);
    };

    // Fetch the operator's existing attachments when the Browse tab opens.
    // Client-side filter to image-ish URLs since the backend doesn't carry
    // a MIME flag — best-effort by file extension.
    React.useEffect(() => {
        if (!open || tab !== 'browse') return undefined;
        let cancelled = false;
        setBrowseLoading(true);
        setBrowseError(null);
        dataAPI(GET_LIST, 'attachment', {
            pagination: { page: 1, perPage: 60 },
            sort: { field: 'id', order: 'DESC' },
            filter: {},
        })
            .then((res) => {
                if (cancelled) return;
                setBrowseRows(res?.data || []);
            })
            .catch((e) => {
                if (!cancelled) setBrowseError(e?.message || 'Could not load attachments');
            })
            .finally(() => {
                if (!cancelled) setBrowseLoading(false);
            });
        return () => { cancelled = true; };
    }, [open, tab, dataAPI]);

    const imageRows = React.useMemo(
        () => (browseRows || []).filter((r) => isImageUrl(r.url || r.file)),
        [browseRows],
    );

    return (
        <Dialog open={!!open} onClose={close} maxWidth="md" fullWidth>
            <DialogTitle>Insert image</DialogTitle>
            <DialogContent dividers>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab value="upload" label="Upload new" />
                    <Tab value="browse" label="Browse existing" />
                </Tabs>
                {tab === 'upload' && (
                    <Box>
                        <Box
                            className={`${classes.dropzone} ${dragOver ? classes.dropzoneActive : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                        >
                            {uploading ? (
                                <CircularProgress />
                            ) : (
                                <>
                                    <CloudUploadIcon sx={{ fontSize: 40, color: '#7080a0', mb: 1 }} />
                                    <Typography variant="body1">
                                        Drag &amp; drop an image here, or click to choose a file.
                                    </Typography>
                                </>
                            )}
                        </Box>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={onFileInputChange}
                        />
                        {uploadError && (
                            <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>
                        )}
                    </Box>
                )}
                {tab === 'browse' && (
                    <Box>
                        {browseLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {browseError && (
                            <Alert severity="error" sx={{ mb: 2 }}>{browseError}</Alert>
                        )}
                        {!browseLoading && !browseError && imageRows.length === 0 && (
                            <Typography color="text.secondary" textAlign="center" py={4}>
                                No image attachments yet. Use the Upload new tab to add one.
                            </Typography>
                        )}
                        {!browseLoading && imageRows.length > 0 && (
                            <ImageList cols={4} rowHeight={140} gap={8}>
                                {imageRows.map((r) => (
                                    <ImageListItem
                                        key={r.id}
                                        onClick={() => finishPick({ id: r.id, url: r.url, name: r.name })}
                                        sx={{ cursor: 'pointer', '&:hover': { outline: '2px solid #007AFF' } }}
                                    >
                                        <img src={r.url} alt={r.name || ''} loading="lazy" />
                                        <ImageListItemBar title={r.name || `#${r.id}`} />
                                    </ImageListItem>
                                ))}
                            </ImageList>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default InlineImagePickerDialog;
